import { useState } from 'react';
import { InformationCircleIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface LegalDisclaimersProps {
  variant?: 'banner' | 'modal' | 'inline';
  showCorrectionsLink?: boolean;
  onCorrectionsClick?: () => void;
}

const LegalDisclaimers = ({ 
  variant = 'inline', 
  showCorrectionsLink = true,
  onCorrectionsClick 
}: LegalDisclaimersProps) => {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible && variant === 'banner') {
    return null;
  }

  const disclaimerContent = (
    <div className="space-y-4">
      <div className="flex items-start space-x-3">
        <InformationCircleIcon className="w-6 h-6 text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="font-semibold text-gray-900 mb-2">AI-Generated Data Notice</h4>
          <p className="text-sm text-gray-700 leading-relaxed">
            The manufacturing cost breakdowns, environmental impact assessments, and labor condition analyses 
            on ProductTrace are generated using artificial intelligence and publicly available information. 
            While we strive for accuracy, this data should be considered <strong>estimates</strong> and may not 
            reflect actual manufacturing costs, environmental impacts, or working conditions.
          </p>
        </div>
      </div>

      <div className="flex items-start space-x-3">
        <ExclamationTriangleIcon className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="font-semibold text-gray-900 mb-2">Data Accuracy & Verification</h4>
          <p className="text-sm text-gray-700 leading-relaxed">
            ProductTrace aggregates information from various sources and uses AI analysis to estimate costs, 
            environmental impacts, and supply chain details. <strong>This information has not been independently 
            verified</strong> and should not be used as the sole basis for business, investment, or purchasing decisions. 
            We encourage users to conduct their own research and verification.
          </p>
        </div>
      </div>

      <div className="flex items-start space-x-3">
        <InformationCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="font-semibold text-gray-900 mb-2">Help Us Improve</h4>
          <p className="text-sm text-gray-700 leading-relaxed">
            We're committed to improving data accuracy through community input. If you have verified information 
            about manufacturing costs, environmental impacts, or supply chain details, please share corrections 
            with supporting sources. Your contributions help make ProductTrace more reliable for everyone.
          </p>
          {showCorrectionsLink && (
            <button
              onClick={onCorrectionsClick}
              className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-700 underline"
            >
              Submit a correction or feedback →
            </button>
          )}
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Intended Use</h4>
        <p className="text-xs text-gray-600 leading-relaxed">
          ProductTrace is designed for educational and research purposes to promote transparency in manufacturing 
          and supply chains. The platform provides estimated insights into product composition, costs, and impacts 
          to help consumers, researchers, and businesses better understand global manufacturing. For critical 
          business decisions, please consult verified industry sources and conduct independent analysis.
        </p>
      </div>
    </div>
  );

  if (variant === 'banner') {
    return (
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
        <div className="flex justify-between">
          <div className="flex-1">
            {disclaimerContent}
          </div>
          <button
            onClick={() => setIsVisible(false)}
            className="ml-4 flex-shrink-0 text-blue-400 hover:text-blue-600"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  if (variant === 'modal') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Data Accuracy & Legal Notice</h3>
            <button
              onClick={() => setIsVisible(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          {disclaimerContent}
          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setIsVisible(false)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              I Understand
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Inline variant
  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
      {disclaimerContent}
    </div>
  );
};

export default LegalDisclaimers; 